/**
 * Keyboard shortcut utilities for cross-platform support
 *
 * Mac: Uses Command (⌘) for most shortcuts
 * Windows/Linux: Uses Alt for most shortcuts, except Ctrl+Enter for submit
 */

/**
 * Detect if running on macOS
 */
export function isMac(): boolean {
  // Use userAgentData if available, fallback to userAgent
  if (typeof navigator !== 'undefined') {
    // Modern approach with userAgentData
    if ('userAgentData' in navigator && (navigator as any).userAgentData?.platform) {
      return (navigator as any).userAgentData.platform.toUpperCase().indexOf('MAC') >= 0;
    }
    // Fallback to userAgent string
    return navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
  }
  return false;
}

/**
 * Get the modifier key name for the current platform
 * @param type - Type of modifier: 'nav' for navigation, 'submit' for form submission
 */
export function getModifierKey(type: 'nav' | 'submit' = 'nav'): string {
  if (type === 'submit') {
    return isMac() ? 'Cmd' : 'Ctrl';
  }
  return isMac() ? 'Cmd' : 'Alt';
}

/**
 * Check if the correct modifier key is pressed for navigation
 */
export function isNavModifier(e: KeyboardEvent | React.KeyboardEvent): boolean {
  if (isMac()) {
    return e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey;
  } else {
    return e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;
  }
}

/**
 * Check if the correct modifier key is pressed for form submission
 * Mac: Cmd+Enter
 * Windows/Linux: Ctrl+Enter
 */
export function isSubmitModifier(e: KeyboardEvent | React.KeyboardEvent): boolean {
  if (isMac()) {
    return e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey;
  } else {
    return e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
  }
}

/**
 * Format keyboard shortcut for display
 */
export function formatShortcut(key: string, type: 'nav' | 'submit' = 'nav'): string {
  const modifier = getModifierKey(type);
  return `${modifier}+${key}`;
}
